import React, { useContext } from 'react'
import FormPhone from '../FormPhone'
import FormEmail from '../FormEmail'
import { ContextApp } from '@monorepo/service'
import { useLogin } from '@monorepo/api'
import { getCommonAssetPath, getGeoAssetPath } from '../../geo/config'
import { useGeoRuntime } from '../../geo/useGeoRuntime'

import { useTrackingEvents } from '../../hooks/useTrackingEvents'
import { useInitialMode } from '../../hooks/useInitialMode'
import { useExternalScript } from '../../hooks/useExternalScript'
import { useUpdateContent } from '../../hooks/useUpdateContent'
import { useGlobalGeoLang } from '../../hooks/useGlobalGeoLang'
import { useLanguageSync } from '../../hooks/useLanguageSync'
import Input from '../Input'

const Layout = () => {
  const { isCdaCheck, isCdaBalancerInfoLoading, cdaBalancerInfo } = useContext(ContextApp)

  const { geo, geoConfig, language, languageMeta, allowedLanguages, getCopyKey } = useGeoRuntime()

  const { origin, redirectToLoginPage } = useLogin({
    isBalancerInfoLoading: isCdaBalancerInfoLoading,
    balancerInfo: cdaBalancerInfo,
  })

  const hasMultipleLanguages = allowedLanguages.length > 1

  const { mode, setMode, initialStyle } = useInitialMode()
  const scriptLoaded = useExternalScript()

  useTrackingEvents(isCdaCheck)
  useUpdateContent(scriptLoaded, geo, mode)
  useGlobalGeoLang(geo, language)
  useLanguageSync({
    allowedLanguages,
    language,
    languageMeta,
    scriptLoaded,
  })

  const logoMobileSrc = getCommonAssetPath('logoMobile.svg')
  const logoTabletSrc = getCommonAssetPath('logoTablet.svg')
  const logoDesktopSrc = getCommonAssetPath('logoDesktop.svg')

  const renderDecorativeAssets = (assets: typeof geoConfig.layout.decorElements.mobile) =>
    assets.map(({ file, className }) => (
      <img
        key={className}
        src={getCommonAssetPath(file)}
        alt="decorative element"
        loading="lazy"
        aria-hidden="true"
        className={className}
      />
    ))

  return (
    <>
      <header className="header">
        <nav className="nav">
          <div className="nav__logo">
            <picture>
              <source srcSet={logoDesktopSrc} media="(min-width: 1200px)" />
              <source srcSet={logoTabletSrc} media="(min-width: 768px)" />
              <img src={logoMobileSrc} alt="BoostWin" className="estop" />
            </picture>
          </div>
          <div className="nav__inner">
            <div className="nav__btn">
              <a
                className="button"
                data-cy="login-button"
                data-origin={origin}
                data-i18n="global.login"
                onClick={redirectToLoginPage}
              >
                Войти
              </a>
            </div>

            <div className="nav__languages">
              <div className="langSelector" id="languageSwitcher">
                <div className="selected">
                  <img src={languageMeta.flag} alt={languageMeta.label.toLowerCase()} />
                  <span>{languageMeta.label}</span>

                  {hasMultipleLanguages && (
                    <div className="selected-icon">
                      <img
                        src="data:image/svg+xml,%3csvg%20width='12'%20height='8'%20fill='none'%20xmlns='http://www.w3.org/2000/svg'%3e%3cpath%20d='M1.41.58L6%205.17%2010.59.58%2012%202%206%208%200%202%201.41.58z'%20fill='%23fff'/%3e%3c/svg%3e"
                        alt="Win"
                      />
                    </div>
                  )}
                </div>

                {hasMultipleLanguages && <ul className="dropdown-list" />}
              </div>
            </div>
          </div>
        </nav>
      </header>

      <main className="main" data-geo={geo}>
        <h1 className="nav__title">
          <div className="title" data-i18n="global.title">
            Регистрируйся и забирай бонус!
          </div>
        </h1>
        <section className="hero">
          <div className="hero__container container">
            <div className="hero__form" id="hero__form">
              <div className="hero__form-logo">
                <picture>
                  <source srcSet={logoDesktopSrc} media="(min-width: 1200px)" />
                  <source srcSet={logoTabletSrc} media="(min-width: 768px)" />
                  <img src={logoMobileSrc} alt="BoostWin" className="estop" />
                </picture>
              </div>
              <div className="hero__form-title">
                <span className="hero__form-text" data-i18n="hero.title">
                  Ваш бонус на первый депозит:{' '}
                </span>
                <br />
                <span className="hero__form-bonus" data-i18n={getCopyKey('hero.title-line-1')}>
                </span>
                <br />
              </div>
              <div className="hero__form-content">
                <div className="hero__form-toggle" style={initialStyle}>
                  <div
                    className={mode === 'email' ? 'active' : ''}
                    onClick={() => setMode('email')}
                    data-i18n="hero.toggle.email"
                  >
                    Почта
                  </div>
                  <div
                    className={mode === 'phone' ? 'active' : ''}
                    onClick={() => setMode('phone')}
                    data-i18n="hero.toggle.phone"
                  >
                    Телефон
                  </div>
                </div>

                {mode === 'phone' ? <FormPhone /> : <FormEmail />}
              </div>
            </div>
          </div>
        </section>
        <picture>
          <source
            media="(min-width: 1200px)"
            srcSet={getGeoAssetPath(geo, geoConfig.layout.mainElement1.desktop)}
          />
          <source
            media="(min-width: 768px)"
            srcSet={getGeoAssetPath(geo, geoConfig.layout.mainElement1.tablet)}
          />
          <img
            src={getGeoAssetPath(geo, geoConfig.layout.mainElement1.mobile)}
            alt="main element"
            className={`estop absolute ${geoConfig.layout.mainElement1.className}`}
            fetchPriority="high"
          />
        </picture>
        <picture>
          <source
            media="(min-width: 1200px)"
            srcSet={getGeoAssetPath(geo, geoConfig.layout.mainElement2.desktop)}
          />
          <source
            media="(min-width: 768px)"
            srcSet={getGeoAssetPath(geo, geoConfig.layout.mainElement2.tablet)}
          />
          <img
            src={getGeoAssetPath(geo, geoConfig.layout.mainElement2.mobile)}
            alt="main element"
            className={`estop absolute ${geoConfig.layout.mainElement2.className}`}
            fetchPriority="high"
          />
        </picture>
        <div className="d-md-none">
          {renderDecorativeAssets(geoConfig.layout.decorElements.mobile)}
        </div>

        <div className="d-none d-md-block d-xl-none">
          {renderDecorativeAssets(geoConfig.layout.decorElements.tablet)}
        </div>

        <div className="d-none d-xl-block">
          {renderDecorativeAssets(geoConfig.layout.decorElements.desktop)}
        </div>
      </main>
    </>
  )
}

export default Layout
